import React, { useState, ChangeEvent, FormEvent, useRef, useEffect } from 'react';
import axios from 'axios';
import ReferenceDropdown from '../components/ReferenceDropdown';
import ReferenceMultiSelect from './ReferenceMultiSelect';

// --- INTERFACES ---

interface ExistingDoc {
  name: string;
  url: string;
  id?: number; 
}

interface EditPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  patientId: string | number | null;
  initialData?: Partial<PatientData>;
}

// Matches DB Table: patient_clients
interface ClientData {
  id?: number;
  salutation: string;
  client_name: string;
  relationship: string;
  contact_number_1: string;
  contact_number_2: string;
  email: string;
}

interface TreatingDetail {
  doctor: string;
  department: string;
  hospital: string;
}

interface FileWithId {
  id: string;
  file: File;
}

interface DutyDetail {
  duty_type: string;
  duty_type_specify: string;
  duty_schedule: string;
  duty_rate: string;
}

interface PatientData {
  // Case Config & Admin
  status: 'Active' | 'Suspended' | 'Stopped';
  case_start_date: string;
  registration_date: string;
  
  // UI State for Duties (Array)
  duty_details: DutyDetail[];

  // Flat fields for backend compatibility (Primary Duty)
  duty_type: string;
  duty_type_specify: string;
  duty_schedule: string;
  duty_rate: string;

  case_manager: string;
  referred_by: string[];
  billing_pattern: string;
  customer_category: string;

  // Patient Info
  patient_salutation: string;
  patient_name: string;
  age: string;
  sex: string;
  citizenship: string;
  blood_group: string;
  height: string;
  weight: string;

  // Location & Contact
  address: string;
  location_link: string;

  // Medical & Treatment
  hospital: string;
  treating_details: TreatingDetail[];
  
  // UI State for Medical History (Strings)
  medical_history_list: string[]; 
  present_status_list: string[];
  general_information: string;

  // Other Excel Fields
  languages: string[];
  religion: string;

  // UI State for Clients
  clients_info: ClientData[];

  // Files
  photo: FileWithId[];
  discharge_files: FileWithId[];
  reg_files: FileWithId[];
  id_proof_files: FileWithId[];
}

interface MultiFileUploaderProps {
  files: FileWithId[];
  onChange: (files: FileWithId[]) => void;
  label?: string;
  accept?: string;
}

// --- CONSTANTS ---

const DUTY_TYPES = [
  '08 Hrs', '12 Day', '12 Dual', '12 Night', '24 Hrs', 'Nursing Visit', 'Physiotherapy', 'Medical Equipment', 'Consumables'
];
const BLOOD_GROUPS = [
  'A +ve', 'A -ve', 'B +ve', 'B -ve', 'AB +ve', 'AB -ve', 'O +ve', 'O -ve'
];

// --- WIZARD CONFIG ---
const FORM_STEPS = [
  { id: 'overview', title: 'Case Overview' },
  { id: 'patient', title: 'Patient Profile' },
  { id: 'operations', title: 'Operations' },
  { id: 'documents', title: 'Documents' },
];

// --- STYLES ---

const styles: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modalWrapper: { backgroundColor: 'white', borderRadius: '12px', width: '98%', maxWidth: '1600px', height: '96vh', display: 'flex', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', position: 'relative' },
  sidebar: { width: '260px', backgroundColor: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarHeader: { padding: '1.25rem', borderBottom: '1px solid #e2e8f0', backgroundColor: '#fff' },
  sidebarContent: { padding: '1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  stepTab: { padding: '12px 14px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', border: '1px solid transparent' },
  activeTab: { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' },
  stepTitle: { fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' },
  activeText: { color: '#1d4ed8' },
  mainArea: { flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#fff', overflow: 'hidden' },
  scrollArea: { flex: 1, overflowY: 'auto', padding: '1.5rem 2.5rem' },
  sectionBlock: { marginBottom: '2rem' },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#0f172a', margin: '0 0 1rem 0', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' },
  rowFlex: { display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap', width: '100%', marginBottom: '1rem', overflow: 'visible' },
  formGroup: { display: 'flex', flexDirection: 'column', position: 'relative', gap: '0.3rem', flex: 1, minWidth: '110px' },
  formGroupAuto: { display: 'flex', flexDirection: 'column', position: 'relative', gap: '0.3rem', flex: '0 0 auto' },
  label: { fontSize: '0.75rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', outline: 'none', transition: 'border-color 0.2s', width: '100%', boxSizing: 'border-box', height: '40px' },
  footer: { padding: '1rem 2.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' },
  button: { padding: '0.6rem 1.2rem', fontSize: '0.875rem', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s' },
  cancelButton: { backgroundColor: '#e2e8f0', color: '#475569' },
  saveButton: { backgroundColor: '#3b82f6', color: 'white' },
  smallBtn: { padding: '0 0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #cbd5e1', cursor: 'pointer', backgroundColor: '#fff', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  addBtn: { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '0 1rem', cursor: 'pointer', borderRadius: '4px', fontWeight: 600, height: '40px', display: 'flex', alignItems: 'center', flexShrink: 0 },
  removeBtn: { backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' },
  uploadBox: { border: '1px dashed #94a3b8', borderRadius: '6px', padding: '0 0.5rem', textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px' },
  progressBarContainer: { marginTop: '10px', width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' },
  progressBarFill: { height: '100%', backgroundColor: '#3b82f6', transition: 'width 0.3s ease' },
  progressOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
  existingFileBox: { marginTop: '0.5rem', padding: '0.5rem', border: '1px solid #bfdbfe', borderRadius: '6px', backgroundColor: '#eff6ff', fontSize: '0.85rem' },
  existingFileLink: { color: '#2563eb', textDecoration: 'none', fontWeight: 500 },
  card: { backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '6px', marginBottom: '0.75rem', border: '1px solid #e2e8f0' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: 600, color: '#64748b' },
  textArea: { padding: '0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', minHeight: '80px', fontFamily: 'inherit' },
  hint: { fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' },
  fileList: { marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  fileItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.6rem', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem' },
};

// --- HELPER COMPONENTS ---

const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const getFileNameFromUrl = (url: string) => {
  if (!url) return '';
  try {
    const parts = url.split('/');
    return decodeURIComponent(parts[parts.length - 1].split('?')[0]);
  } catch (e) {
    return 'View File';
  }
};

const ExistingFileDisplay: React.FC<{ url: string | null; label?: string; onRemove?: () => void }> = ({ url, label, onRemove }) => {
  if (!url) return null;
  const fileName = label || getFileNameFromUrl(url) || 'Document';
    
  return (
    <div style={styles.existingFileBox}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <span style={{ color: '#1e40af', marginRight: '5px', flexShrink: 0 }}>✓</span>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...styles.existingFileLink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={fileName}>
                {fileName}
            </a>
        </div>
        {onRemove && (
            <button 
                type="button" 
                onClick={onRemove}
                style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.2rem', marginLeft: '10px', lineHeight: 1 }}
                title="Remove file"
            >
                &times;
            </button>
        )}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
        {label ? `${label} - ` : ''} Uploading new replaces this.
      </div>
    </div>
  );
};

const MultiFileUploader: React.FC<MultiFileUploaderProps> = ({ files, onChange, label, accept }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles: FileWithId[] = Array.from(e.target.files).map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file
      }));
      onChange([...files, ...newFiles]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id: string) => {
    onChange(files.filter(f => f.id !== id));
  };

  return (
    <div style={styles.formGroup}>
      {label && <label style={styles.label}>{label}</label>}
      <div 
        style={styles.uploadBox} 
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const droppedFiles = Array.from(e.dataTransfer.files).map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            file
          }));
          onChange([...files, ...droppedFiles]);
        }}
      >
        <input 
          type="file" 
          multiple 
          accept={accept}
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          onChange={handleFileChange} 
        />
        <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Browse or Drag</span>
        <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Multiple files supported</small>
      </div>

      {files.length > 0 && (
        <div style={styles.fileList}>
          {files.map((item) => (
            <div key={item.id} style={styles.fileItem}>
              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', maxWidth: '85%' }}>
                <span 
                    title={item.file.name}
                    style={{ fontWeight: 500, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {item.file.name.length > 25 ? item.file.name.substring(0, 25) + '...' : item.file.name}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{formatBytes(item.file.size)}</span>
              </div>
              <button 
                type="button" 
                onClick={() => removeFile(item.id)} 
                style={{ ...styles.smallBtn, ...styles.removeBtn, border: 'none', padding: '0.1rem 0.4rem', fontSize: '1.2rem', lineHeight: '1rem', height: 'auto' }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---

const EditPatientModal: React.FC<EditPatientModalProps> = ({ isOpen, onClose, onSuccess, patientId, initialData }) => {
  const initialClient: ClientData = {
    salutation: '',
    client_name: '',
    relationship: '',
    contact_number_1: '',
    contact_number_2: '',
    email: ''
  };
  const initialTreatingDetail: TreatingDetail = { doctor: '', department: '', hospital: '' };

  const defaultState: PatientData = {
    status: 'Active',
    case_start_date: '',
    registration_date: '',
    
    duty_details: [{ 
      duty_type: '08 Hrs', 
      duty_type_specify: '', 
      duty_schedule: '', 
      duty_rate: '' 
    }],
    
    duty_type: '08 Hrs',
    duty_type_specify: '',
    duty_schedule: '',
    duty_rate: '',

    case_manager: '',
    referred_by: [''],
    billing_pattern: 'Monthly',
    customer_category: 'Middle Class',
    patient_salutation: '',
    patient_name: '',
    age: '',
    sex: '',
    citizenship: '',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    location_link: '',
    hospital: '',
    treating_details: [{ ...initialTreatingDetail }],
    medical_history_list: [''], 
    present_status_list: [''],
    general_information: '',
    languages: [''],
    religion: 'Hindu',
    clients_info: [{ ...initialClient }],
    photo: [],
    discharge_files: [],
    reg_files: [],
    id_proof_files: [],
  };

  const [patientData, setPatientData] = useState<PatientData>(defaultState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeStepId, setActiveStepId] = useState<string>(FORM_STEPS[0].id);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Storage for existing docs mapped from backend
  const [existingFiles, setExistingFiles] = useState<{[key: string]: ExistingDoc[]}>({});
  const [deletedDocIds, setDeletedDocIds] = useState<number[]>([]);

  // Helper to mark file for deletion
  const removeExistingFile = (key: string, docId: number | undefined, index: number) => {
    if (docId) setDeletedDocIds(prev => [...prev, docId]);
    setExistingFiles(prev => ({ 
        ...prev, 
        [key]: prev[key].filter((_, i) => i !== index) 
    }));
  };

  // --- POPULATE DATA ON OPEN ---
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;

      // Reset states on open
      setExistingFiles({});
      setDeletedDocIds([]);
      setUploadProgress(0);

      let mergedData = { ...defaultState };
      let sourceData: any = initialData || null;

      if (patientId) {
        try {
          setIsLoading(true);
          const token = localStorage.getItem('accessToken');
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          
          const response = await axios.get(`${API_BASE_URL}/api/patients/${patientId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.data) {
             sourceData = response.data;
          }
        } catch (error) {
          console.error("Failed to fetch patient data", error);
        } finally {
          setIsLoading(false);
        }
      }

      if (sourceData) {
        // --- MAP API RESPONSE TO REACT STATE ---
        mergedData = { ...mergedData, ...sourceData };

        // 1. Map Clients
        const clientsFromApi = sourceData.patient_clients || sourceData.clients || sourceData.clients_info;
        if (clientsFromApi && Array.isArray(clientsFromApi) && clientsFromApi.length > 0) {
            mergedData.clients_info = clientsFromApi.map((c: any) => ({
                id: c.id,
                salutation: c.salutation || '',
                client_name: c.client_name || c.name || '',
                relationship: c.relationship || '',
                contact_number_1: c.contact_number_1 || c.contact_number || '',
                contact_number_2: c.contact_number_2 || c.secondary_contact_number || '',
                email: c.email || ''
            }));
        }

        // 2. Map Medical History
        const historyData = sourceData.patient_medical_history || sourceData.medical_history;
        const conditions: string[] = [];
        const statuses: string[] = [];
        if (historyData && Array.isArray(historyData)) {
            historyData.forEach((h: any) => {
                const val = typeof h === 'string' ? h : (h.condition_name || '');
                if (val.startsWith("Present Status: ")) {
                    statuses.push(val.replace("Present Status: ", ""));
                } else {
                    conditions.push(val);
                }
            });
        }
        mergedData.medical_history_list = conditions.length > 0 ? conditions : [''];
        mergedData.present_status_list = statuses.length > 0 ? statuses : [''];

        // 3. Map Duty Details (Convert to Array for UI)
        let loadedDutyDetails: DutyDetail[] = [];
        if (sourceData.duty_details && Array.isArray(sourceData.duty_details) && sourceData.duty_details.length > 0) {
            loadedDutyDetails = sourceData.duty_details.map((d: any) => ({
                duty_type: d.duty_type || '08 Hrs',
                duty_type_specify: d.duty_type_specify || '',
                duty_schedule: d.duty_schedule || '',
                duty_rate: d.duty_rate || ''
            }));
        } else {
             loadedDutyDetails.push({
                duty_type: sourceData.duty_type || '08 Hrs',
                duty_type_specify: sourceData.duty_type_specify || '',
                duty_schedule: sourceData.duty_schedule || '',
                duty_rate: sourceData.duty_rate || ''
            });
        }
        mergedData.duty_details = loadedDutyDetails;

        // 4. Map Hospital (single field)
        mergedData.hospital = sourceData.hospital ||
          (sourceData.treating_details?.[0]?.hospital) || '';

        // Map Treating Details (doctor/department only)
        if (sourceData.treating_details && Array.isArray(sourceData.treating_details) && sourceData.treating_details.length > 0) {
          mergedData.treating_details = sourceData.treating_details.map((t: any) => ({
            doctor: t.doctor || '',
            department: t.department || '',
          }));
        } else if (sourceData.treating_department || sourceData.treating_doctor) {
          if (!mergedData.treating_details.length) mergedData.treating_details = [{...initialTreatingDetail}];
          if (sourceData.treating_department) mergedData.treating_details[0].department = sourceData.treating_department;
          if (sourceData.treating_doctor) mergedData.treating_details[0].doctor = sourceData.treating_doctor;
        }

        // --- MAP EXISTING FILES ---
        const extractUrl = (data: any): string | null => {
            if (!data) return null;
            if (typeof data === 'string') return data;
            return data.url || data.file || data.file_url || data.attachment || null;
        };

        const allDocs = sourceData.documents && Array.isArray(sourceData.documents) ? sourceData.documents : [];
        
        const findDocs = (searchTerm: string): ExistingDoc[] => {
            if (!searchTerm) return [];
            const lowerSearch = searchTerm.toLowerCase();
            return allDocs.filter((d: any) => {
                const name = (d.document_name || d.name || '').toLowerCase();
                const type = (d.type || d.document_type || '').toLowerCase();
                return name.includes(lowerSearch) || type.includes(lowerSearch);
            }).map((d: any) => ({
                name: d.document_name || d.name || 'Document',
                url: extractUrl(d) || '',
                id: d.id
            })).filter((d: ExistingDoc) => d.url);
        };

        const fileMap: {[key: string]: ExistingDoc[]} = {
            photo: [
                ...(extractUrl(sourceData.photo_url || sourceData.photo) ? [{ name: 'Photo', url: extractUrl(sourceData.photo_url || sourceData.photo)!, id: undefined }] : []),
                ...findDocs('photo')
            ],
            discharge_files: [
                ...(extractUrl(sourceData.discharge_summary || sourceData.discharge_summary_url) ? [{ name: 'Discharge Summary', url: extractUrl(sourceData.discharge_summary || sourceData.discharge_summary_url)!, id: undefined }] : []),
                ...findDocs('discharge')
            ],
            reg_files: [
                ...(extractUrl(sourceData.registration_form || sourceData.registration_form_url) ? [{ name: 'Registration Form', url: extractUrl(sourceData.registration_form || sourceData.registration_form_url)!, id: undefined }] : []),
                ...findDocs('registration')
            ],
            id_proof_files: [
                ...(extractUrl(sourceData.id_proof_url || sourceData.customer_id_proof || sourceData.customer_id_proof_url) 
                    ? [{ name: 'ID Proof', url: extractUrl(sourceData.id_proof_url || sourceData.customer_id_proof || sourceData.customer_id_proof_url)!, id: undefined }] 
                    : []),
                ...findDocs('id proof'),
                ...findDocs('customer id'),
                ...findDocs('customer_id_proof'),
                ...findDocs('id_proof')
            ],
        };

        // Deduplicate
        Object.keys(fileMap).forEach(key => {
            const uniqueDocs: ExistingDoc[] = [];
            const seenUrls = new Set();
            fileMap[key].forEach(doc => {
                if (!seenUrls.has(doc.url)) {
                    seenUrls.add(doc.url);
                    uniqueDocs.push(doc);
                }
            });
            fileMap[key] = uniqueDocs;
        });

        setExistingFiles(fileMap);
      }

      // --- SANITIZATION ---
      Object.keys(mergedData).forEach(key => {
        const k = key as keyof PatientData;
        if (mergedData[k] === null || mergedData[k] === undefined) {
             // @ts-ignore
             mergedData[k] = ''; 
        }
      });

      if (!mergedData.treating_details || mergedData.treating_details.length === 0) mergedData.treating_details = [{ ...initialTreatingDetail }];
      if (!mergedData.clients_info || mergedData.clients_info.length === 0) mergedData.clients_info = [{ ...initialClient }];
      if (!mergedData.referred_by || mergedData.referred_by.length === 0) mergedData.referred_by = [''];
      if (!mergedData.medical_history_list || mergedData.medical_history_list.length === 0) mergedData.medical_history_list = [''];
      if (!mergedData.present_status_list || mergedData.present_status_list.length === 0) mergedData.present_status_list = [''];
      if (!mergedData.languages || mergedData.languages.length === 0) mergedData.languages = [''];

      mergedData.photo = [];
      mergedData.discharge_files = [];
      mergedData.reg_files = [];
      mergedData.id_proof_files = [];

      setPatientData(mergedData as PatientData);
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patientId, initialData]);


  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPatientData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilesChange = (name: keyof PatientData, files: FileWithId[]) => {
      setPatientData(prev => ({ ...prev, [name]: files }));
  };

  // --- DUTY LOGIC ---
  const handleDutyChange = (index: number, field: keyof DutyDetail, value: string) => {
    const updatedDetails = [...patientData.duty_details];
    
    if (field === 'duty_type') {
      updatedDetails[index].duty_schedule = ''; 
      if (value !== 'Specify') {
        updatedDetails[index].duty_type_specify = '';
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    setPatientData(prev => ({ ...prev, duty_details: updatedDetails }));
  };

  const addDutyRow = () => {
    if (patientData.duty_details.length < 4) {
      setPatientData(prev => ({
        ...prev,
        duty_details: [...prev.duty_details, { duty_type: '08 Hrs', duty_type_specify: '', duty_schedule: '', duty_rate: '' }]
      }));
    }
  };

  const removeDutyRow = (index: number) => {
    if (patientData.duty_details.length > 1) {
      const updatedDetails = patientData.duty_details.filter((_, i) => i !== index);
      setPatientData(prev => ({ ...prev, duty_details: updatedDetails }));
    }
  };

  const handleMedicalHistoryChange = (index: number, value: string) => {
    const updatedList = [...patientData.medical_history_list];
    updatedList[index] = value;
    setPatientData(prev => ({ ...prev, medical_history_list: updatedList }));
  };
  const addMedicalHistory = () => {
    setPatientData(prev => ({ ...prev, medical_history_list: [...prev.medical_history_list, ''] }));
  };
  const removeMedicalHistory = (index: number) => {
    setPatientData(prev => ({ ...prev, medical_history_list: prev.medical_history_list.filter((_, i) => i !== index) }));
  };

  const handlePresentStatusChange = (index: number, value: string) => {
    const updatedList = [...patientData.present_status_list];
    updatedList[index] = value;
    setPatientData(prev => ({ ...prev, present_status_list: updatedList }));
  };
  const addPresentStatus = () => {
    setPatientData(prev => ({ ...prev, present_status_list: [...prev.present_status_list, ''] }));
  };
  const removePresentStatus = (index: number) => {
    setPatientData(prev => ({ ...prev, present_status_list: prev.present_status_list.filter((_, i) => i !== index) }));
  };

  const handleClientChange = (index: number, field: keyof ClientData, value: string) => {
    const updatedClients = [...patientData.clients_info];
    updatedClients[index] = { ...updatedClients[index], [field]: value };
    setPatientData(prev => ({ ...prev, clients_info: updatedClients }));
  };
  const addClient = () => {
    if (patientData.clients_info.length < 3) {
      setPatientData(prev => ({ ...prev, clients_info: [...prev.clients_info, { ...initialClient }] }));
    } else {
      alert("Maximum 3 clients allowed.");
    }
  };
  const removeClient = (index: number) => {
    const updatedClients = patientData.clients_info.filter((_, i) => i !== index);
    setPatientData(prev => ({ ...prev, clients_info: updatedClients }));
  };

  const handleTreatingChange = (index: number, field: keyof TreatingDetail, value: string) => {
    const updatedDetails = [...patientData.treating_details];
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    setPatientData(prev => ({ ...prev, treating_details: updatedDetails }));
  };
  const addTreatingDetail = () => {
    if (patientData.treating_details.length < 3) {
      setPatientData(prev => ({ ...prev, treating_details: [...prev.treating_details, { ...initialTreatingDetail }] }));
    }
  };
  const removeTreatingDetail = (index: number) => {
    if (index === 0) return;
    const updatedDetails = patientData.treating_details.filter((_, i) => i !== index);
    setPatientData(prev => ({ ...prev, treating_details: updatedDetails }));
  };

  const handleReferredByChange = (index: number, value: string) => {
    const updatedArray = [...patientData.referred_by];
    updatedArray[index] = value;
    setPatientData(prev => ({ ...prev, referred_by: updatedArray }));
  };
  const addReferredBy = () => {
    if (patientData.referred_by.length < 3) {
      setPatientData(prev => ({ ...prev, referred_by: [...prev.referred_by, ''] }));
    }
  };
  const removeReferredBy = (index: number) => {
    if (index === 0) return;
    const updatedArray = patientData.referred_by.filter((_, i) => i !== index);
    setPatientData(prev => ({ ...prev, referred_by: updatedArray }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!patientId) {
      alert("Error: Missing Patient ID");
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    const token = localStorage.getItem('accessToken');

    const cleanValue = (val: string | undefined) => {
       if (val === undefined || val === null) return null;
       if (typeof val === 'string' && val.trim() === '') return null;
       return val;
    };

    // --- EXTRACT PRIMARY DUTY FOR FLATTENED FIELDS ---
    const primaryDuty = patientData.duty_details[0] || {};

    const dataToSend = {
      ...patientData,
      photo: undefined,
      discharge_files: undefined,
      reg_files: undefined,
      id_proof_files: undefined,

      referred_by: patientData.referred_by.filter(r => r.trim() !== ''),
      present_status_list: patientData.present_status_list.filter(p => p.trim() !== ''),
      medical_history_list: patientData.medical_history_list.filter(m => m.trim() !== ''),
      languages: patientData.languages.filter(l => l.trim() !== ''),
      
      clients_info: patientData.clients_info
        .filter(c => c.client_name.trim() !== '')
        .map(c => ({
            id: c.id,
            salutation: c.salutation,
            name: c.client_name,
            relationship: c.relationship,
            contact_number: c.contact_number_1,
            secondary_contact_number: c.contact_number_2,
            email: c.email
        })),

      // Hospital is one value for the whole case and is carried on these rows,
      // so a row must survive when EITHER the doctor or the hospital is filled.
      // Keying the filter on the doctor alone dropped the only row whenever the
      // doctor was left blank, taking the hospital down with it — the save then
      // reported success having stored neither. Matches AddPatientModal.
      treating_details: patientData.treating_details
        .filter(t => t.doctor.trim() !== '' || (patientData.hospital || '').trim() !== '')
        .map(t => ({ ...t, hospital: patientData.hospital })),
      treating_department: patientData.treating_details.length > 0 ? patientData.treating_details[0].department : null,

      // --- CLEAN AND SEND FULL DUTY ARRAY ---
      duty_details: patientData.duty_details.filter(d => d.duty_type !== ''),

      // --- SEND FLATTENED DUTY DATA (For Dashboard Table) ---
      duty_type: primaryDuty.duty_type || '',
      duty_type_specify: primaryDuty.duty_type_specify || '',
      duty_schedule: primaryDuty.duty_schedule || '',
      duty_rate: cleanValue(primaryDuty.duty_rate),

      height: cleanValue(patientData.height),
      weight: cleanValue(patientData.weight),
      case_start_date: cleanValue(patientData.case_start_date), 
      registration_date: cleanValue(patientData.registration_date),

      // Includes Deleted Files
      deleted_document_ids: deletedDocIds,
    };

    const formData = new FormData();
    formData.append('patient_data', JSON.stringify(dataToSend));

    if (patientData.photo.length > 0) formData.append('photo', patientData.photo[0].file);
    patientData.discharge_files.forEach(f => formData.append('discharge_summary', f.file));
    patientData.reg_files.forEach(f => formData.append('registration_form', f.file));
    patientData.id_proof_files.forEach(f => formData.append('customer_id_proof', f.file));

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      await axios.patch(`${API_BASE_URL}/api/patients/${patientId}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
            }
        }
      });

      setUploadProgress(100);
      await new Promise(r => setTimeout(r, 500));

      alert('Patient updated successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.response) {
        alert(`Failed: ${JSON.stringify(error.response.data.detail || error.response.data)}`);
      } else {
        alert('Failed to update patient.');
      }
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleNavigation = (direction: 'next' | 'prev') => {
    const currentIndex = FORM_STEPS.findIndex(s => s.id === activeStepId);
    if (direction === 'next' && currentIndex < FORM_STEPS.length - 1) setActiveStepId(FORM_STEPS[currentIndex + 1].id);
    else if (direction === 'prev' && currentIndex > 0) setActiveStepId(FORM_STEPS[currentIndex - 1].id);
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isOpen) return null;

  const isFirstStep = FORM_STEPS.findIndex(s => s.id === activeStepId) === 0;
  const isLastStep = FORM_STEPS.findIndex(s => s.id === activeStepId) === FORM_STEPS.length - 1;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalWrapper} onClick={e => e.stopPropagation()}>

        {/* Progress Overlay */}
        {isSubmitting && (
          <div style={styles.progressOverlay}>
            <div style={{ width: '400px', textAlign: 'center', backgroundColor: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b' }}>Updating Registration...</h3>
              <div style={styles.progressBarContainer}><div style={{ ...styles.progressBarFill, width: `${uploadProgress}%` }}></div></div>
              <p style={{ marginTop: '10px', color: '#64748b', fontWeight: 600 }}>{uploadProgress}% Complete</p>
            </div>
          </div>
        )}

        {/* SIDEBAR */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>Edit Client Registration</h2>
          </div>
          <div style={styles.sidebarContent}>
            {FORM_STEPS.map((step) => (
              <div key={step.id} onClick={() => setActiveStepId(step.id)} style={{ ...styles.stepTab, ...(activeStepId === step.id ? styles.activeTab : {}) }}>
                <div style={{ ...styles.stepTitle, ...(activeStepId === step.id ? styles.activeText : {}) }}>{step.title}</div>
              </div>
            ))}
          </div>
        </div>

        {/* MAIN AREA */}
        <form style={styles.mainArea} onSubmit={handleSubmit}>
          <div style={styles.scrollArea} ref={scrollContainerRef}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
              <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>

            {isLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading patient data...</div>
            ) : (
              <>
                {/* ===================== STEP 1: CASE OVERVIEW ===================== */}
                {activeStepId === 'overview' && (
                  <div className="fade-in">

                    {/* Client Information */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Client Information</h3>
                      <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div style={{ flexShrink: 0 }}>
                          <label style={{ ...styles.label, display: 'block', marginBottom: '0.4rem' }}>Photo</label>
                          <div
                            onClick={() => document.getElementById('patient-photo-input-edit')?.click()}
                            style={{ width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed #94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', backgroundColor: '#f8fafc', boxSizing: 'border-box' }}
                          >
                            {patientData.photo.length > 0
                              ? <img src={URL.createObjectURL(patientData.photo[0].file)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : existingFiles['photo']?.[0]?.url
                                ? <img src={existingFiles['photo'][0].url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <><span style={{ fontSize: '1.8rem', color: '#94a3b8' }}>📷</span><span style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>Upload</span></>
                            }
                          </div>
                          <input id="patient-photo-input-edit" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleFilesChange('photo', [{ id: Math.random().toString(36).substr(2, 9), file: e.target.files[0] }]); }} />
                          {patientData.photo.length > 0 && (
                            <span onClick={() => handleFilesChange('photo', [])} style={{ fontSize: '0.75rem', color: '#ef4444', cursor: 'pointer', fontWeight: 600, display: 'block', textAlign: 'center', marginTop: '4px' }}>Remove</span>
                          )}
                        </div>
                        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <div style={{ width: '110px', flexShrink: 0 }}>
                              <ReferenceDropdown label="Title" category="salutation" value={patientData.clients_info[0]?.salutation || ''} onChange={(val) => handleClientChange(0, 'salutation', val)} />
                            </div>
                            <div style={{ ...styles.formGroup, flex: 1 }}>
                              <label style={styles.label}>Client Full Name</label>
                              <input style={styles.input} value={patientData.clients_info[0]?.client_name || ''} onChange={(e) => handleClientChange(0, 'client_name', e.target.value)} placeholder="Full Name" required />
                            </div>
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Relationship</label>
                            <input style={styles.input} value={patientData.clients_info[0]?.relationship || ''} onChange={(e) => handleClientChange(0, 'relationship', e.target.value)} placeholder="e.g. Son" />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Primary Contact</label>
                            <input style={styles.input} value={patientData.clients_info[0]?.contact_number_1 || ''} onChange={(e) => handleClientChange(0, 'contact_number_1', e.target.value)} placeholder="+91 00000 00000" />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Secondary Contact</label>
                            <input style={styles.input} value={patientData.clients_info[0]?.contact_number_2 || ''} onChange={(e) => handleClientChange(0, 'contact_number_2', e.target.value)} placeholder="+91 00000 00000" />
                          </div>
                          <div style={{ ...styles.formGroup, gridColumn: 'span 2' }}>
                            <label style={styles.label}>Email</label>
                            <input style={styles.input} type="email" value={patientData.clients_info[0]?.email || ''} onChange={(e) => handleClientChange(0, 'email', e.target.value)} placeholder="client@example.com" />
                          </div>
                        </div>
                      </div>

                      {patientData.clients_info.slice(1).map((client, i) => {
                        const idx = i + 1;
                        return (
                          <div key={idx} style={{ ...styles.card, marginBottom: '0.5rem' }}>
                            <div style={styles.cardHeader}>
                              <span>CLIENT #{idx + 1}</span>
                              <button type="button" onClick={() => removeClient(idx)} style={{ ...styles.smallBtn, ...styles.removeBtn, fontSize: '0.8rem', padding: '0 0.6rem' }}>Remove</button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <div style={{ width: '110px', flexShrink: 0 }}>
                                  <ReferenceDropdown label="Title" category="salutation" value={client.salutation || ''} onChange={(val) => handleClientChange(idx, 'salutation', val)} />
                                </div>
                                <div style={{ ...styles.formGroup, flex: 1 }}>
                                  <label style={styles.label}>Client Name</label>
                                  <input style={styles.input} value={client.client_name} onChange={(e) => handleClientChange(idx, 'client_name', e.target.value)} placeholder="Full Name" />
                                </div>
                              </div>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Relationship</label>
                                <input style={styles.input} value={client.relationship} onChange={(e) => handleClientChange(idx, 'relationship', e.target.value)} />
                              </div>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Primary Contact</label>
                                <input style={styles.input} value={client.contact_number_1} onChange={(e) => handleClientChange(idx, 'contact_number_1', e.target.value)} />
                              </div>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Secondary Contact</label>
                                <input style={styles.input} value={client.contact_number_2} onChange={(e) => handleClientChange(idx, 'contact_number_2', e.target.value)} />
                              </div>
                              <div style={{ ...styles.formGroup, gridColumn: 'span 2' }}>
                                <label style={styles.label}>Email</label>
                                <input style={styles.input} type="email" value={client.email} onChange={(e) => handleClientChange(idx, 'email', e.target.value)} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {patientData.clients_info.length < 3 && (
                        <button type="button" onClick={addClient} style={styles.addBtn}>+ Add Another Client</button>
                      )}
                    </div>

                    {/* Patient Information */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Patient Information</h3>
                      <div style={styles.rowFlex}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flex: 2, minWidth: '180px' }}>
                          <div style={{ width: '110px', flexShrink: 0 }}>
                            <ReferenceDropdown label="Title" category="salutation" value={patientData.patient_salutation} onChange={(val) => setPatientData(prev => ({ ...prev, patient_salutation: val }))} />
                          </div>
                          <div style={{ ...styles.formGroup, flex: 1 }}>
                            <label style={styles.label}>Patient Full Name</label>
                            <input style={styles.input} name="patient_name" value={patientData.patient_name} onChange={handleChange} placeholder="Full Name" required />
                          </div>
                        </div>
                        <div style={{ ...styles.formGroup, flex: 0.5, minWidth: '80px' }}>
                          <label style={styles.label}>Age</label>
                          <input style={styles.input} name="age" value={patientData.age} onChange={handleChange} placeholder="e.g. 65" />
                        </div>
                        <div style={{ ...styles.formGroup, flex: 0.6, minWidth: '90px' }}>
                          <label style={styles.label}>Sex</label>
                          <select style={styles.input} name="sex" value={patientData.sex} onChange={handleChange}>
                            <option value="">-</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Treating Details */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Treating Details</h3>
                      <div style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
                        {patientData.treating_details.map((detail, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap', width: '100%', marginBottom: '0.5rem' }}>
                            <div style={{ flex: 2, minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1 }}>
                                  <ReferenceDropdown label="Doctor Name" category="doctor_name" value={detail.doctor} onChange={(val) => handleTreatingChange(idx, 'doctor', val)} />
                                </div>
                                {idx !== 0 && (
                                  <button type="button" onClick={() => removeTreatingDetail(idx)} style={{ ...styles.smallBtn, ...styles.removeBtn, width: '40px', padding: 0 }}>&times;</button>
                                )}
                                {idx === patientData.treating_details.length - 1 && patientData.treating_details.length < 3 && (
                                  <button type="button" onClick={addTreatingDetail} style={styles.addBtn}>+ Add Doctor</button>
                                )}
                              </div>
                              <span style={styles.hint}>e.g. Dr. Yash Bhushan - Orthopedic</span>
                            </div>
                            {idx === 0 ? (
                              <div style={{ flex: 2, minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                <ReferenceDropdown label="Hospital" category="hospital" value={patientData.hospital} onChange={(val) => setPatientData(prev => ({ ...prev, hospital: val }))} />
                                <span style={styles.hint}>e.g. Apollo Speciality Hospital - Jayanagar</span>
                              </div>
                            ) : (
                              <div style={{ flex: 2 }} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Location */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Location</h3>
                      <div style={styles.rowFlex}>
                        <div style={{ ...styles.formGroup, flex: 2, minWidth: '200px' }}>
                          <label style={styles.label}>Residential Address</label>
                          <textarea style={styles.textArea} name="address" value={patientData.address} onChange={handleChange} placeholder="Full residential address" />
                        </div>
                        <div style={{ ...styles.formGroup, flex: 1.5, minWidth: '180px' }}>
                          <label style={styles.label}>Google Maps Link</label>
                          <input style={styles.input} name="location_link" value={patientData.location_link} onChange={handleChange} placeholder="https://maps.google.com/..." />
                        </div>
                      </div>
                    </div>

                    {/* Registration */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Registration</h3>
                      <div style={styles.rowFlex}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Registration Date</label>
                          <input type="date" style={styles.input} name="registration_date" value={patientData.registration_date} onChange={handleChange} required />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Case Start Date</label>
                          <input type="date" style={styles.input} name="case_start_date" value={patientData.case_start_date} onChange={handleChange} />
                        </div>
                        {patientData.referred_by.map((ref, idx) => (
                          <div key={idx} style={styles.formGroup}>
                            <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-end' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <ReferenceDropdown
                                  label={idx === 0 ? 'Referred By' : `Referred By #${idx + 1}`}
                                  category="referred_by"
                                  value={ref}
                                  onChange={(val) => handleReferredByChange(idx, val)}
                                />
                              </div>
                              {idx > 0 && <button type="button" onClick={() => removeReferredBy(idx)} style={{ ...styles.smallBtn, ...styles.removeBtn }}>&times;</button>}
                            </div>
                          </div>
                        ))}
                        {patientData.referred_by.length < 3 && (
                          <div style={styles.formGroupAuto}>
                            <label style={{ ...styles.label, visibility: 'hidden' }}>add</label>
                            <button type="button" onClick={addReferredBy} style={styles.addBtn}>+ Add Ref</button>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                )}

                {/* ===================== STEP 2: PATIENT PROFILE ===================== */}
                {activeStepId === 'patient' && (
                  <div className="fade-in">

                    {/* Additional Patient Details */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Additional Patient Details</h3>
                      <div style={styles.rowFlex}>
                        <div style={styles.formGroup}>
                          <ReferenceDropdown
                            label="Citizenship"
                            category="citizenship"
                            value={patientData.citizenship}
                            onChange={(val) => setPatientData(prev => ({ ...prev, citizenship: val }))}
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Blood Group</label>
                          <select style={styles.input} name="blood_group" value={patientData.blood_group} onChange={handleChange}>
                            <option value="">-</option>
                            {BLOOD_GROUPS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                          </select>
                        </div>
                        <div style={{ ...styles.formGroup, flex: 0.5, minWidth: '80px' }}>
                          <label style={styles.label}>Height</label>
                          <input style={styles.input} name="height" value={patientData.height} onChange={handleChange} placeholder="cm" />
                        </div>
                        <div style={{ ...styles.formGroup, flex: 0.5, minWidth: '80px' }}>
                          <label style={styles.label}>Weight</label>
                          <input style={styles.input} name="weight" value={patientData.weight} onChange={handleChange} placeholder="kg" />
                        </div>
                      </div>
                    </div>

                    {/* Languages */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Languages</h3>
                      <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                        <ReferenceMultiSelect
                          label="Patient Languages"
                          category="languages"
                          selectedValues={patientData.languages}
                          onChange={(newLanguages) => setPatientData(prev => ({ ...prev, languages: newLanguages }))}
                        />
                      </div>
                    </div>

                    {/* Medical Condition */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Medical Condition</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                        {patientData.medical_history_list.map((item, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexGrow: 1, maxWidth: '450px' }}>
                            <div style={{ flex: 1 }}>
                              <ReferenceDropdown label={`Condition #${index + 1}`} category="medical_history" value={item} onChange={(val) => handleMedicalHistoryChange(index, val)} />
                            </div>
                            {index > 0 && (
                              <button type="button" onClick={() => removeMedicalHistory(index)} style={{ ...styles.smallBtn, ...styles.removeBtn }}>✕</button>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={addMedicalHistory} style={styles.addBtn}>+ Add Condition</button>
                      </div>
                    </div>

                    {/* Present Medical Status */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Present Medical Status</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                        {patientData.present_status_list.map((item, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexGrow: 1, maxWidth: '450px' }}>
                            <div style={{ flex: 1 }}>
                              <ReferenceDropdown label={`Status #${index + 1}`} category="present_status" value={item} onChange={(val) => handlePresentStatusChange(index, val)} />
                            </div>
                            {index > 0 && (
                              <button type="button" onClick={() => removePresentStatus(index)} style={{ ...styles.smallBtn, ...styles.removeBtn }}>✕</button>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={addPresentStatus} style={styles.addBtn}>+ Add Status</button>
                      </div>
                    </div>

                    {/* General Information */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>General Information</h3>
                      <textarea style={styles.textArea} name="general_information" value={patientData.general_information} onChange={handleChange} placeholder="Enter any general notes or information." />
                    </div>

                  </div>
                )}

                {/* ===================== STEP 3: OPERATIONS ===================== */}
                {activeStepId === 'operations' && (
                  <div className="fade-in">

                    {/* Rate & Duty Type */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Rate & Duty Type</h3>
                      <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                        {patientData.duty_details.map((detail, index) => (
                          <div key={index} style={{ marginBottom: index < patientData.duty_details.length - 1 ? '1rem' : 0, paddingBottom: index < patientData.duty_details.length - 1 ? '1rem' : 0, borderBottom: index < patientData.duty_details.length - 1 ? '1px dashed #cbd5e1' : 'none' }}>
                            <div style={styles.rowFlex}>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Type of Duty {index + 1}</label>
                                {detail.duty_type === 'Specify' ? (
                                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <input style={{ ...styles.input, flex: 1, minWidth: 0 }} placeholder="Specify Type" value={detail.duty_type_specify} onChange={(e) => handleDutyChange(index, 'duty_type_specify', e.target.value)} autoFocus />
                                    <button type="button" style={styles.smallBtn} onClick={() => handleDutyChange(index, 'duty_type', '08 Hrs')}>x</button>
                                  </div>
                                ) : (
                                  <select style={styles.input} value={detail.duty_type} onChange={(e) => handleDutyChange(index, 'duty_type', e.target.value)}>
                                    <option value="">Select...</option>
                                    {DUTY_TYPES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    <option value="Specify" style={{ fontWeight: 'bold', color: '#3b82f6' }}>Specify...</option>
                                  </select>
                                )}
                              </div>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Rate (Cost)</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <input style={{ ...styles.input, flex: 1 }} value={detail.duty_rate} onChange={(e) => handleDutyChange(index, 'duty_rate', e.target.value)} placeholder="e.g. 1500" type="number" />
                                  {patientData.duty_details.length > 1 && (
                                    <button type="button" style={{ ...styles.smallBtn, ...styles.removeBtn }} onClick={() => removeDutyRow(index)}>X</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {patientData.duty_details.length < 4 && (
                          <button type="button" onClick={addDutyRow} style={{ ...styles.addBtn, marginTop: '0.5rem' }}>+ Add Duty</button>
                        )}
                      </div>
                    </div>

                    {/* Billing & Category */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Billing & Category</h3>
                      <div style={styles.rowFlex}>
                        <div style={styles.formGroup}>
                          <ReferenceDropdown label="Billing Pattern" category="billing_pattern" value={patientData.billing_pattern} onChange={(val) => setPatientData(prev => ({ ...prev, billing_pattern: val }))} />
                        </div>
                        <div style={styles.formGroup}>
                          <ReferenceDropdown label="Customer Category" category="customer_category" value={patientData.customer_category} onChange={(val) => setPatientData(prev => ({ ...prev, customer_category: val }))} />
                        </div>
                      </div>
                    </div>

                    {/* Case Management */}
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Case Management</h3>
                      <div style={styles.rowFlex}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Case Status</label>
                          <select style={styles.input} name="status" value={patientData.status} onChange={handleChange}>
                            <option value="Active">Active</option>
                            <option value="Suspended">Suspended</option>
                            <option value="Stopped">Stopped</option>
                          </select>
                        </div>
                        <div style={styles.formGroup}>
                          <ReferenceDropdown label="Case Manager" category="case_manager" value={patientData.case_manager} onChange={(val) => setPatientData(prev => ({ ...prev, case_manager: val }))} />
                        </div>
                      </div>
                    </div>

                  </div>
                )}

                {/* ===================== STEP 4: DOCUMENTS ===================== */}
                {activeStepId === 'documents' && (
                  <div className="fade-in">
                    <div style={styles.sectionBlock}>
                      <h3 style={styles.sectionTitle}>Documents</h3>
                      <div style={styles.rowFlex}>
                        <div style={{ ...styles.formGroup, minWidth: '200px' }}>
                          <label style={styles.label}>Discharge Summary</label>
                          <MultiFileUploader files={patientData.discharge_files} onChange={(files) => handleFilesChange('discharge_files', files)} />
                          {existingFiles['discharge_files']?.map((doc, i) => (
                            <ExistingFileDisplay key={i} url={doc.url} label={doc.name} onRemove={() => removeExistingFile('discharge_files', doc.id, i)} />
                          ))}
                        </div>
                        <div style={{ ...styles.formGroup, minWidth: '200px' }}>
                          <label style={styles.label}>Registration Form</label>
                          <MultiFileUploader files={patientData.reg_files} onChange={(files) => handleFilesChange('reg_files', files)} />
                          {existingFiles['reg_files']?.map((doc, i) => (
                            <ExistingFileDisplay key={i} url={doc.url} label={doc.name} onRemove={() => removeExistingFile('reg_files', doc.id, i)} />
                          ))}
                        </div>
                        <div style={{ ...styles.formGroup, minWidth: '200px' }}>
                          <label style={styles.label}>Customer ID Proof</label>
                          <MultiFileUploader files={patientData.id_proof_files} onChange={(files) => handleFilesChange('id_proof_files', files)} />
                          {existingFiles['id_proof_files']?.map((doc, i) => (
                            <ExistingFileDisplay key={i} url={doc.url} label={doc.name} onRemove={() => removeExistingFile('id_proof_files', doc.id, i)} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* FOOTER */}
          {!isLoading && (
            <div style={styles.footer}>
              <div>
                {!isFirstStep && (
                  <button type="button" onClick={() => handleNavigation('prev')} style={{ ...styles.button, ...styles.cancelButton }}>Back</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {!isLastStep ? (
                  <button type="button" onClick={() => handleNavigation('next')} style={{ ...styles.button, ...styles.saveButton }}>Next Step</button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{ ...styles.button, ...styles.saveButton, backgroundColor: '#059669', opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                  >
                    {isSubmitting ? 'Saving...' : 'Update Registration'}
                  </button>
                )}
              </div>
            </div>
          )}
        </form>

      </div>
    </div>
  );
};

export default EditPatientModal;